import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, ImagePlus, Trash2, Loader2, Store, Plus, MinusCircle } from 'lucide-react';
import ChefHeader from '../shared/ui/ChefHeader';
import {
  Toggle,
  PrimaryButton,
  LoadingView,
  EmptyState,
} from '../shared/ui';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchMenuItems,
  saveMenuItem,
  deleteMenuItem,
  uploadMenuItemImage,
} from '../shared/services/userService';
import {
  isFoodRelated,
  defaultCategories,
  ALLERGEN_TYPES,
} from '../shared/menuCategories';
import ConfirmDialog from '../components/menu/ConfirmDialog';

const INPUT =
  'w-full px-3 py-2.5 rounded-xl bg-gray-100 text-sm outline-none focus:ring-2 focus:ring-[#FF6B35]/40';

// Mirrors iOS MenuDetailView in add/edit mode: image picker with preview,
// item details (name, description, price, category, availability, prep
// time), save + delete with confirm.
// Collapsed-by-default card (mirrors the iOS DisclosureGroup sections).
function CollapsibleSection({ title, children }) {
  return (
    <details className="group">
      <summary className="list-none cursor-pointer select-none flex items-center justify-between bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3.5 group-open:rounded-b-none [&::-webkit-details-marker]:hidden">
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        <span className="text-gray-400 transition-transform group-open:rotate-90">›</span>
      </summary>
      <div className="bg-white rounded-b-2xl border border-t-0 border-gray-100 shadow-sm px-4 divide-y divide-gray-100">
        {children}
      </div>
    </details>
  );
}

export default function MenuItemEditPage() {
  const { itemId } = useParams();
  const isNew = !itemId;
  const navigate = useNavigate();
  const { restaurantId, restaurant } = useAuth();
  const fileRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [trackStock, setTrackStock] = useState(false);
  const [stockCount, setStockCount] = useState('');
  // Size/variant rows for retail items: {id, name, stock} — empty stock = unlimited
  const [variants, setVariants] = useState([]);
  const [isSpicy, setIsSpicy] = useState(false);
  const [isVegan, setIsVegan] = useState(false);
  const [isOrganic, setIsOrganic] = useState(false);
  const [nutritionAvailable, setNutritionAvailable] = useState(false);
  const [calories, setCalories] = useState('');
  const [allergens, setAllergens] = useState(() => new Set());
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imageURL, setImageURL] = useState('');

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!restaurantId) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    fetchMenuItems(restaurantId)
      .then((list) => {
        if (cancelled) return;
        setItems(list);
        if (!isNew) {
          const it = list.find((i) => i.id === itemId);
          if (it) {
            setName(it.name);
            setDescription(it.description);
            setPrice(it.price ? String(it.price) : '');
            setCategory(it.category || '');
            setPrepTime(it.preparationTime ? String(it.preparationTime) : '');
            setIsAvailable(it.isAvailable);
            setTrackStock(it.trackStock === true);
            setStockCount(it.trackStock ? String(it.stockCount ?? 0) : '');
            setVariants(
              (it.variants || []).map((v) => ({
                id: v.id || Math.random().toString(36).slice(2),
                name: v.name || '',
                stock: v.stock == null ? '' : String(v.stock),
              }))
            );
            const dietary = it.dietaryRestrictions || [];
            setIsSpicy(dietary.includes('Spicy'));
            setIsVegan(dietary.includes('Vegan'));
            setIsOrganic(dietary.includes('Organic'));
            setNutritionAvailable((it.calories ?? 0) > 0);
            setCalories(it.calories ? String(it.calories) : '');
            setAllergens(new Set(it.allergens || []));
            setAvailablePeriods(it.availablePeriods || []);
            setImageURL(it.imageURL || '');
          } else {
            setNotFound(true);
          }
        }
        setLoaded(true);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message);
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantId, itemId, isNew]);

  // iOS MenuCategory.defaultCategories(for: restaurantType) + any categories
  // already used by this restaurant's items or stored on the restaurant doc.
  const foodRelated = isFoodRelated(restaurant?.restaurantType);
  const categories = useMemo(() => {
    const set = new Set(defaultCategories(restaurant?.restaurantType));
    items.forEach((i) => i.category && set.add(i.category));
    (restaurant?.raw?.menuCategories || []).forEach((c) => c && set.add(c));
    return [...set];
  }, [items, restaurant]);

  const previewURL = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : null),
    [imageFile]
  );
  useEffect(
    () => () => {
      if (previewURL) URL.revokeObjectURL(previewURL);
    },
    [previewURL]
  );

  // Total stock derived from sizes: only when every named size has a quantity.
  const sizeTotal = useMemo(() => {
    const named = variants.filter((v) => v.name.trim());
    if (!named.length || named.some((v) => v.stock === '')) return null;
    return named.reduce((sum, v) => sum + (parseInt(v.stock, 10) || 0), 0);
  }, [variants]);

  const priceValue = parseFloat(price);
  const isValid = name.trim().length > 0 && !isNaN(priceValue) && priceValue > 0;

  const validationMessage = () => {
    if (!name.trim()) return 'Please enter an item name';
    if (!price.trim()) return 'Please enter a price';
    if (isNaN(priceValue)) return 'Please enter a valid price';
    if (priceValue <= 0) return 'Price must be greater than 0';
    return null;
  };

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (f) setImageFile(f);
    e.target.value = '';
  };

  const onSave = async () => {
    if (!isValid || saving || !restaurantId) return;
    setSaving(true);
    setError(null);
    try {
      let finalImageURL = imageURL;
      if (imageFile) {
        finalImageURL = await uploadMenuItemImage(
          restaurantId,
          itemId || 'new',
          imageFile
        );
      }
      // iOS contract: dietary flags -> dietaryRestrictions[], calories only
      // when nutrition info is marked available, allergens as string array.
      // availablePeriods: iOS writes [] for new items and preserves on edit.
      const dietaryRestrictions = foodRelated
        ? [
            ...(isSpicy ? ['Spicy'] : []),
            ...(isVegan ? ['Vegan'] : []),
            ...(isOrganic ? ['Organic'] : []),
          ]
        : [];
      await saveMenuItem({
        id: itemId || undefined,
        restaurantId,
        name: name.trim(),
        description: description.trim(),
        price: priceValue,
        category: category.trim() || 'Other',
        isAvailable,
        trackStock: sizeTotal !== null ? true : trackStock,
        stockCount: sizeTotal !== null ? sizeTotal : trackStock ? parseInt(stockCount, 10) || 0 : 0,
        variants: variants
          .filter((v) => v.name.trim())
          .map((v) => ({
            id: v.id,
            name: v.name.trim(),
            stock: v.stock === '' ? null : parseInt(v.stock, 10) || 0,
          })),
        preparationTime: parseInt(prepTime, 10) || 0,
        imageURL: finalImageURL || '',
        dietaryRestrictions,
        calories: foodRelated && nutritionAvailable ? parseInt(calories, 10) || 0 : 0,
        allergens: foodRelated ? [...allergens] : [],
        availablePeriods: isNew ? [] : availablePeriods,
      });
      navigate('/business/menu');
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!itemId) return;
    setDeleting(true);
    try {
      await deleteMenuItem(restaurantId, itemId);
      navigate('/business/menu');
    } catch (e) {
      setError(e.message);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const shownImage = previewURL || imageURL;

  return (
    <div>
      <ChefHeader
        title={isNew ? 'Add Item' : 'Edit Item'}
        right={
          <button
            type="button"
            onClick={() => navigate('/business/menu')}
            aria-label="Close"
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-500 active:bg-gray-100"
          >
            <X size={20} />
          </button>
        }
      />

      {!restaurantId ? (
        <EmptyState
          icon={Store}
          title="No Store Found"
          subtitle="Set up your restaurant profile before adding menu items."
          action={
            <PrimaryButton
              className="mt-3 max-w-[220px]"
              onClick={() => navigate('/business/profile')}
            >
              Set Up Your Store
            </PrimaryButton>
          }
        />
      ) : !loaded ? (
        <LoadingView label="Loading item..." />
      ) : notFound ? (
        <EmptyState
          icon={Store}
          title="Item Not Found"
          subtitle="This menu item may have been deleted."
          action={
            <PrimaryButton
              className="mt-3 max-w-[220px]"
              onClick={() => navigate('/business/menu')}
            >
              Back to Menu
            </PrimaryButton>
          }
        />
      ) : (
        <div className="px-4 pt-3 pb-6 space-y-4">
          {/* Image section (iOS: preview + PhotosPicker) */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 space-y-3">
            {shownImage ? (
              <img
                src={shownImage}
                alt={name || 'Menu item'}
                className="w-full max-h-[280px] object-contain rounded-xl bg-gray-50"
              />
            ) : (
              <div className="h-[200px] rounded-xl bg-gray-100 flex items-center justify-center text-gray-300">
                <ImagePlus size={44} />
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickImage}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full py-2.5 rounded-xl bg-orange-50 text-[#FF6B35] text-sm font-semibold flex items-center justify-center gap-2 active:bg-orange-100"
            >
              <ImagePlus size={16} />
              {shownImage ? 'Change Image' : 'Select Image'}
            </button>
          </div>

          {/* Item Details */}
          <div>
            <p className="px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Item Details
            </p>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Name *
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Item name"
                  className={INPUT}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                  rows={3}
                  className={`${INPUT} resize-none`}
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    Price ($) *
                  </label>
                  <input
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                    inputMode="decimal"
                    type="number"
                    min="0"
                    step="0.01"
                    className={INPUT}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    Prep Time (min)
                  </label>
                  <input
                    value={prepTime}
                    onChange={(e) => setPrepTime(e.target.value)}
                    placeholder="15"
                    inputMode="numeric"
                    type="number"
                    min="0"
                    step="1"
                    className={INPUT}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Category
                </label>
                {categories.length > 0 ? (
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className={INPUT}
                  >
                    <option value="">Select a category</option>
                    {category && !categories.includes(category) && (
                      <option value={category}>{category}</option>
                    )}
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="Category (e.g. Burgers)"
                    className={INPUT}
                  />
                )}
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-sm font-medium text-gray-800">
                  Available
                </span>
                <Toggle checked={isAvailable} onChange={setIsAvailable} />
              </div>
            </div>
          </div>

          {/* Inventory — optional stock tracking for retail-style items.
              When on, each purchase deducts the ordered quantity and the
              item shows Out of Stock at 0. Collapsed by default like iOS. */}
          <CollapsibleSection title="Inventory">
            {sizeTotal !== null ? (
              <div className="py-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800">Total in Stock</span>
                  <span className="text-sm font-semibold text-gray-900">{sizeTotal}</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  Calculated from the sizes below — each purchase deducts from its
                  size and the total updates automatically.
                </p>
              </div>
            ) : (
            <div className="flex items-center justify-between py-3">
              <div>
                <span className="text-sm font-medium text-gray-800">Track Stock</span>
                <p className="text-xs text-gray-400 mt-0.5">
                  Deduct from stock on every purchase
                </p>
              </div>
              <Toggle checked={trackStock} onChange={setTrackStock} />
            </div>
            )}
            {sizeTotal === null && trackStock && (
              <div className="flex items-center justify-between py-3">
                <span className="text-sm font-medium text-gray-800">Quantity in Stock</span>
                <input
                  value={stockCount}
                  onChange={(e) => setStockCount(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="0"
                  inputMode="numeric"
                  type="number"
                  min="0"
                  step="1"
                  className="w-24 px-3 py-1.5 rounded-lg bg-gray-100 text-sm text-right outline-none focus:ring-2 focus:ring-[#FF6B35]/40"
                />
              </div>
            )}
          </CollapsibleSection>

          {/* Sizes & Stock — retail items only. Customers pick a size
              before ordering; blank Qty = unlimited stock. */}
          {!foodRelated && (
            <div>
              <p className="px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Sizes &amp; Stock
              </p>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
                {variants.map((v) => (
                  <div key={v.id} className="flex items-center gap-2">
                    <input
                      value={v.name}
                      onChange={(e) =>
                        setVariants((rows) =>
                          rows.map((r) =>
                            r.id === v.id ? { ...r, name: e.target.value } : r
                          )
                        )
                      }
                      placeholder="Size (US 9, M, 32...)"
                      className={`${INPUT} flex-1`}
                    />
                    <input
                      value={v.stock}
                      onChange={(e) =>
                        setVariants((rows) =>
                          rows.map((r) =>
                            r.id === v.id
                              ? { ...r, stock: e.target.value.replace(/[^0-9]/g, '') }
                              : r
                          )
                        )
                      }
                      placeholder="Qty"
                      inputMode="numeric"
                      type="number"
                      min="0"
                      className="w-20 px-3 py-2.5 rounded-xl bg-gray-100 text-sm text-right outline-none focus:ring-2 focus:ring-[#FF6B35]/40"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setVariants((rows) => rows.filter((r) => r.id !== v.id))
                      }
                      aria-label="Remove size"
                      className="text-[#FF3B30] shrink-0"
                    >
                      <MinusCircle size={20} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setVariants((rows) => [
                      ...rows,
                      { id: Math.random().toString(36).slice(2), name: '', stock: '' },
                    ])
                  }
                  className="w-full py-2.5 rounded-xl bg-orange-50 text-[#FF6B35] text-sm font-semibold flex items-center justify-center gap-2 active:bg-orange-100"
                >
                  <Plus size={16} />
                  Add Size
                </button>
                <p className="text-xs text-gray-400">
                  Enter a Qty for every size and total stock is calculated for
                  you. Leave Qty empty for unlimited stock. Customers must pick a
                  size before ordering — e.g. shoe size, waist size, S/M/L.
                </p>
              </div>
            </div>
          )}

          {/* iOS: dietary/nutrition/allergen sections only for food businesses */}
          {foodRelated && (
            <>
              <CollapsibleSection title="Dietary Properties">
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm font-medium text-gray-800">🌶️ Spicy</span>
                  <Toggle checked={isSpicy} onChange={setIsSpicy} />
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm font-medium text-gray-800">🌱 Vegan</span>
                  <Toggle checked={isVegan} onChange={setIsVegan} />
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm font-medium text-gray-800">🍃 Organic</span>
                  <Toggle checked={isOrganic} onChange={setIsOrganic} />
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Nutritional Information">
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm font-medium text-gray-800">
                    Nutrition Information Available
                  </span>
                  <Toggle checked={nutritionAvailable} onChange={setNutritionAvailable} />
                </div>
                {nutritionAvailable && (
                  <div className="flex items-center justify-between py-3">
                    <span className="text-sm font-medium text-gray-800">Calories</span>
                    <input
                      value={calories}
                      onChange={(e) => setCalories(e.target.value)}
                      placeholder="0"
                      inputMode="numeric"
                      type="number"
                      min="0"
                      className="w-24 px-3 py-1.5 rounded-lg bg-gray-100 text-sm text-right outline-none focus:ring-2 focus:ring-[#FF6B35]/40"
                    />
                  </div>
                )}
              </CollapsibleSection>

              <CollapsibleSection title="Allergens (Big 9)">
                {ALLERGEN_TYPES.map((a) => (
                  <div key={a} className="flex items-center justify-between py-3">
                    <span className="text-sm font-medium text-gray-800">{a}</span>
                    <Toggle
                      checked={allergens.has(a)}
                      onChange={(on) =>
                        setAllergens((prev) => {
                          const next = new Set(prev);
                          on ? next.add(a) : next.delete(a);
                          return next;
                        })
                      }
                    />
                  </div>
                ))}
              </CollapsibleSection>
            </>
          )}

          {error && (
            <div className="px-3 py-2 rounded-xl bg-red-50 text-red-600 text-xs font-medium">
              {error}
            </div>
          )}
          {!error && validationMessage() && (
            <div className="px-3 py-2 rounded-xl bg-orange-50 text-orange-600 text-xs font-medium">
              {validationMessage()}
            </div>
          )}

          <PrimaryButton onClick={onSave} disabled={!isValid || saving}>
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                {isNew ? 'Creating Item...' : 'Saving Changes...'}
              </span>
            ) : isNew ? (
              'Create Item'
            ) : (
              'Save Changes'
            )}
          </PrimaryButton>

          {!isNew && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="w-full py-3 rounded-2xl bg-red-50 text-red-500 font-bold text-sm flex items-center justify-center gap-2 active:bg-red-100"
            >
              <Trash2 size={16} />
              Delete Item
            </button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete Menu Item"
        message={`Delete "${name}"? This can't be undone.`}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
